// Display list (DRAW_PLAN §8): every drawable node → cached Path2D (local coordinates, keyed on the immutable
// node object) + world matrix + world bounds + paint order, indexed in an R-tree (rbush, MIT) for culling and
// hit-testing.
//
// `update(doc)` is incremental: the items of a layer's top node are cached on that node object (Immer gives an
// edited node a new object and keeps every untouched one), so a drag in a 10,000-object document rebuilds only
// the dragged object's items. It returns the world rectangles that changed, so only the tiles under them are
// redrawn.
import RBush from 'rbush';
import type { Doc, Layer, Mat2D, Node, Rect, Subpath } from '../model/types';
import { mapRect, mul, I } from '../geometry/mat';
import { shapeToSubpaths, subpathBounds, tracePath } from '../geometry/bezier';
import { commandsToPath2D, layoutText } from '../text/layout';
import { fontEpoch } from '../text/fonts';
import { framePath } from './adPaint';

export type Item = {
  id: string;
  node: Node;
  /** The node directly inside the layer that contains this one (what the Selection tool selects). */
  topId: string;
  layerId: string;
  world: Mat2D;
  /** World bounds including stroke width. */
  bounds: Rect;
  /** World bounds of the outline only (what transforms and the Transform panel use). */
  gbounds: Rect;
  order: number;
  hidden: boolean;
  locked: boolean;
  path: Path2D | null;
  // rbush fields
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

type Geo = { path: Path2D | null; local: Rect | null; subpaths: Subpath[] };
const geoCache = new WeakMap<Node, Geo>();

/** Subpaths of a drawable node in its own coordinates (shape → expanded, path → as is). */
export function nodeSubpaths(n: Node): Subpath[] {
  if (n.kind === 'shape') return shapeToSubpaths(n.shape);
  if (n.kind === 'path') return n.subpaths;
  if (n.kind === 'image') return framePath(n);
  return [];
}

/** M14 text: the glyph outlines as the path, the text box as the bounds (null until the font is loaded). */
function textGeometry(n: Node & { kind: 'text' }): Geo {
  const lay = layoutText(n);
  if (!lay) return { path: null, local: null, subpaths: [] };
  const path = typeof Path2D !== 'undefined' ? commandsToPath2D(lay.commands) : null;
  return { path, local: lay.bounds, subpaths: [] };
}

export function geometry(n: Node): Geo {
  if (n.kind === 'text') return textGeometry(n);
  let g = geoCache.get(n);
  if (!g) {
    const subpaths = nodeSubpaths(n);
    let path: Path2D | null = null;
    if (subpaths.length && typeof Path2D !== 'undefined') {
      path = new Path2D();
      tracePath(path, subpaths);
    }
    g = { path, local: subpathBounds(subpaths), subpaths };
    geoCache.set(n, g);
  }
  return g;
}

/** How far the widest stroke reaches outside the outline (miter joins can poke further). */
function strokePad(n: Node): number {
  let pad = 0;
  for (const s of n.appearance.strokes) {
    const reach = s.align === 'outside' ? s.width : s.align === 'inside' ? 0 : s.width / 2;
    pad = Math.max(pad, reach * (s.join === 'miter' ? Math.min(s.miter, 4) : 1));
    // Arrowheads reach up to ~3.4 stroke widths past the end and to the side.
    if ((s.startArrow && s.startArrow !== 'none') || (s.endArrow && s.endArrow !== 'none'))
      pad = Math.max(pad, 3.5 * Math.max(s.width, 0.5));
  }
  return pad;
}

/** Items of one top node, cached on the node object (valid while the layer's flags are the same). */
type TopEntry = { items: Item[]; layerId: string; hidden: boolean; locked: boolean; epoch: number };
const topCache = new WeakMap<Node, TopEntry>();

function buildTop(top: Node, layer: Layer): Item[] {
  const items: Item[] = [];
  const visit = (n: Node, world: Mat2D, hidden: boolean, locked: boolean) => {
    const m = mul(world, n.transform);
    const h = hidden || !n.visible,
      l = locked || n.locked;
    if (n.kind === 'group') {
      for (const c of n.children) visit(c, m, h, l);
      return;
    }
    const g = geometry(n);
    if (!g.local) return;
    const pad = strokePad(n);
    const b = mapRect(m, {
      x: g.local.x - pad,
      y: g.local.y - pad,
      w: g.local.w + 2 * pad,
      h: g.local.h + 2 * pad,
    });
    items.push({
      id: n.id,
      node: n,
      topId: top.id,
      layerId: layer.id,
      world: m,
      bounds: b,
      gbounds: mapRect(m, g.local),
      order: 0,
      hidden: h,
      locked: l,
      path: g.path,
      minX: b.x,
      minY: b.y,
      maxX: b.x + b.w,
      maxY: b.y + b.h,
    });
  };
  visit(top, I, !layer.visible, layer.locked);
  return items;
}

export class DisplayList {
  doc: Doc | null = null;
  items: Item[] = [];
  byId = new Map<string, Item>();
  tree = new RBush<Item>();
  /** Items added or removed by the last update (the tile renderer leaves out floating ones). */
  changed: Item[] = [];

  /** Forgets the current document: the next update rebuilds everything (fonts finished loading). */
  reset() {
    this.doc = null;
    this.items = [];
    this.byId = new Map();
    this.tree.clear();
  }

  /** Rebuilds from `doc`; returns the world rects that changed (null = everything). */
  update(doc: Doc): Rect[] | null {
    if (doc === this.doc) {
      this.changed = [];
      return [];
    }
    const prev = this.doc;
    const fast = prev ? this.replaceTops(prev, doc) : null;
    if (fast) return fast;
    const items: Item[] = [];
    const fresh: Item[] = [];
    const freshSet = new Set<Item>();
    // "Fresh" = not in the previous list: rebuilt now, or brought back by undo from an older document.
    const prevSet = new Set(this.items);
    // Kept items must stay in the same relative order; an insertion or deletion only shifts the numbers.
    let orderChanged = false;
    let lastKeptOrder = -1;
    for (const layer of doc.layers) {
      const hidden = !layer.visible;
      for (const top of layer.children) {
        let e = topCache.get(top);
        if (
          !e ||
          e.layerId !== layer.id ||
          e.hidden !== hidden ||
          e.locked !== layer.locked ||
          e.epoch !== fontEpoch
        ) {
          e = {
            items: buildTop(top, layer),
            layerId: layer.id,
            hidden,
            locked: layer.locked,
            epoch: fontEpoch,
          };
          topCache.set(top, e);
        }
        for (const it of e.items)
          if (!prevSet.has(it)) {
            fresh.push(it);
            freshSet.add(it);
          }
        for (const it of e.items) {
          if (!freshSet.has(it)) {
            if (it.order < lastKeptOrder) orderChanged = true;
            lastKeptOrder = it.order;
          }
          items.push(it);
        }
      }
    }
    for (let i = 0; i < items.length; i++) items[i]!.order = i;
    const byId = new Map<string, Item>();
    for (const it of items) byId.set(it.id, it);

    let dirty: Rect[] | null = [];
    const keep = new Set(items);
    const changed: Item[] = [...fresh];
    if (!prev || prev.artboards !== doc.artboards) dirty = null;
    if (!prev || fresh.length > 0.5 * items.length + 64) {
      this.tree.clear();
      this.tree.load(items);
      dirty = null;
    } else {
      for (const old of this.items)
        if (!keep.has(old)) {
          this.tree.remove(old);
          dirty?.push(old.bounds);
          changed.push(old);
        }
      for (const it of fresh) {
        this.tree.insert(it);
        dirty?.push(it.bounds);
      }
      // A z-order change (arrange, layer reorder) repaints everything that may overlap differently.
      if (orderChanged) dirty = null;
    }
    this.changed = changed;
    this.doc = doc;
    this.items = items;
    this.byId = byId;
    return dirty;
  }

  /**
   * Fast path for the common edit (a drag, a colour change): same layers, same number of top nodes, a few top
   * nodes replaced by new objects with the same number of items. Their items are swapped in place: O(changed),
   * not O(document). Returns null when the edit does not fit (the full rebuild handles it).
   */
  private replaceTops(prev: Doc, doc: Doc): Rect[] | null {
    if (prev.artboards !== doc.artboards || prev.layers.length !== doc.layers.length) return null;
    const swaps: [Item[], Item[]][] = [];
    for (let l = 0; l < doc.layers.length; l++) {
      const a = prev.layers[l]!,
        b = doc.layers[l]!;
      if (a === b) continue;
      if (
        a.id !== b.id ||
        a.visible !== b.visible ||
        a.locked !== b.locked ||
        a.children.length !== b.children.length
      )
        return null;
      for (let i = 0; i < b.children.length; i++) {
        const oldTop = a.children[i]!,
          top = b.children[i]!;
        if (oldTop === top) continue;
        const oldE = topCache.get(oldTop);
        if (!oldE || swaps.length >= 64) return null;
        let e = topCache.get(top);
        if (
          !e ||
          e.layerId !== b.id ||
          e.hidden !== !b.visible ||
          e.locked !== b.locked ||
          e.epoch !== fontEpoch
        ) {
          e = {
            items: buildTop(top, b),
            layerId: b.id,
            hidden: !b.visible,
            locked: b.locked,
            epoch: fontEpoch,
          };
          topCache.set(top, e);
        }
        if (e.items.length !== oldE.items.length) return null;
        // An existing object moved here (arrange): a reorder, not a replacement.
        if (e.items.some((it) => this.byId.get(it.id) === it)) return null;
        swaps.push([oldE.items, e.items]);
      }
    }
    const items = this.items.slice();
    const dirty: Rect[] = [];
    const changed: Item[] = [];
    for (const [olds, news] of swaps)
      for (let k = 0; k < olds.length; k++) {
        const o = olds[k]!,
          n = news[k]!;
        if (items[o.order] !== o) return null; // the cached old items are not the ones in the list
        n.order = o.order;
        items[o.order] = n;
        this.tree.remove(o);
        this.tree.insert(n);
        this.byId.set(n.id, n);
        dirty.push(o.bounds, n.bounds);
        changed.push(o, n);
      }
    this.items = items;
    this.changed = changed;
    this.doc = doc;
    return dirty;
  }

  /** Items intersecting a world rect, in paint order. */
  query(r: Rect): Item[] {
    return this.tree
      .search({ minX: r.x, minY: r.y, maxX: r.x + r.w, maxY: r.y + r.h })
      .sort((a, b) => a.order - b.order);
  }
}
